function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
// eslint-disable-next-line no-restricted-imports
import { h } from 'panel';
import { SHOULD_SHOW_SERVING_METHOD } from 'apps/feature-flags/src/constants';
import { FeatureFlagApiResponse, Variant } from 'app_api/projects/feature_flags/types';
import { trackFeatureFlagViewPageAction, TrackingEventSource, ViewPageTrackingEvent } from 'apps/feature-flags/src/tracking';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { InputReact } from 'common/components/input';
import { InputLabelReact } from 'common/components/input/label';
import { ModalReact, ModalType, VerticalAlignment } from 'common/components/modal';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { SwitchChangeEvent, SwitchReact } from 'common/components/switch';
import { useTranslation } from 'common/i18n/hooks';
import { useFeatureFlagPageContext } from 'iron/apps/feature-flags/src/components/context';
import { useUpdateFeatureFlag } from 'iron/apps/feature-flags/src/components/hooks/useUpdateFeatureFlag';
import { fetchUsers } from 'common/report/queries/query-api/users-client';
import { Cohort } from 'common/query-builder/cohorts-query-builder/models/cohort';
import { FiltersOperator, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { PropertyType, ResourceType } from 'common/report/constants';
import { ALL_USERS_COHORT } from 'common/query-builder/query-block/properties-menu/constants';
import { StringOperator } from 'common/widgets/property-filter-menu/models/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { ProjectFeatureFlag } from 'common/report/constants';
import { FeatureFlagTestUsers, UserDisplay } from '../test-users';
import { EvalContextSelect } from '../eval-select';
import styles from './index.module.styl';
export interface EditFeatureFlagProps {
  isOpen: boolean;
  onClose: () => void;
  flag: FeatureFlagApiResponse;
}
export function EditFeatureFlag(_ref: EditFeatureFlagProps) {
  var _stagedFlag$ruleset$t, _stagedFlag$ruleset, _currentProject$get$v;
  let {
    isOpen,
    onClose,
    flag
  }: EditFeatureFlagProps = _ref;
  const i18n = useTranslation();
  const currentProject = useCurrentProject();
  const {
    projectId
  } = useFeatureFlagPageContext();
  const updateFeatureFlag = useUpdateFeatureFlag(projectId);
  const extractVariantsFromFlag = (flagData: FeatureFlagApiResponse): Variant[] => {
    const variantList: Variant[] = [];
    flagData.ruleset.variants.forEach(variant => {
      var _variant$description;
      variantList.push({
        key: variant.key,
        description: (_variant$description = variant.description) !== null && _variant$description !== void 0 ? _variant$description : "",
        split: variant.split,
        is_sticky: variant.is_sticky,
        is_control: variant.is_control,
        value: variant.value
      });
    });
    return variantList;
  };

  // Initialize variants state as an array of Variant objects
  const [variants, setVariants] = useState<Variant[]>(() => {
    return extractVariantsFromFlag(flag);
  });
  const [stagedFlag, setStagedFlag] = useState<FeatureFlagApiResponse>(_objectSpread({}, flag));
  const initialUsers = (_stagedFlag$ruleset$t = (_stagedFlag$ruleset = stagedFlag.ruleset) === null || _stagedFlag$ruleset === void 0 || (_stagedFlag$ruleset = _stagedFlag$ruleset.test) === null || _stagedFlag$ruleset === void 0 ? void 0 : _stagedFlag$ruleset.users) !== null && _stagedFlag$ruleset$t !== void 0 ? _stagedFlag$ruleset$t : {};
  const [testUsers, setTestUsers] = useState<UserDisplay[]>(Object.keys(initialUsers).map(key => {
    return {
      email: key,
      distinctId: key,
      variant: initialUsers[key]
    };
  }));
  const [testUsersLoading, setTestUsersLoading] = useState<boolean>(true);

  // Refresh variants and flag details when modal opens or flag changes
  useEffect(() => {
    if (isOpen) {
      setVariants(extractVariantsFromFlag(flag));
      setStagedFlag(_objectSpread({}, flag));
    }
  }, [isOpen, flag]);

  // Toggle flag status between enabled and disabled
  const handleStatusToggle = () => {
    setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
      status: stagedFlag.status === "enabled" ? "disabled" : "enabled"
    }));
  };

  // Validation vars to determine if the Done button should be disabled
  const {
    isFlagNameEmpty,
    isFlagKeyEmpty,
    isButtonDisabled,
    hasEmptyKeys,
    hasDuplicateNames,
    hasInvalidSplits,
    splitsDoNotAddUpTo100
  } = validateEdits();
  const trackUpdatedFlagSettings = (updatedFlag: FeatureFlagApiResponse) => {
    const updates: Partial<FeatureFlagApiResponse> = {};
    if (updatedFlag.name !== flag.name) updates.name = updatedFlag.name;
    if (updatedFlag.key !== flag.key) updates.key = updatedFlag.key;
    if (updatedFlag.status !== flag.status) updates.status = updatedFlag.status;
    if (updatedFlag.serving_method !== flag.serving_method) updates.serving_method = updatedFlag.serving_method;
    if (updatedFlag.context !== flag.context) updates.context = updatedFlag.context;
    if (Object.keys(updates).length > 0) {
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditSimpleSettings,
        source: TrackingEventSource.ViewPageEditSettings,
        featureFlag: flag,
        props: updates
      });
    }
    if (!isEqual(flag.ruleset.variants, updatedFlag.ruleset.variants)) {
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditVariants,
        source: TrackingEventSource.ViewPageEditSettings,
        featureFlag: flag,
        props: {
          existingVariants: flag.ruleset.variants,
          updatedVariants: updatedFlag.ruleset.variants
        }
      });
    }
  };
  const handleUpdate = () => {
    const updatedFlag = _objectSpread({}, stagedFlag);

    // Update the variants in the ruleset
    updatedFlag.ruleset = _objectSpread(_objectSpread({}, updatedFlag.ruleset), {}, {
      variants: variants.map(variant => ({
        key: variant.key,
        description: variant.description,
        value: variant.key,
        is_control: !!variant.is_control,
        split: variant.split,
        is_sticky: variant.is_sticky
      }))
    });
    trackUpdatedFlagSettings(updatedFlag);
    updateFeatureFlag(updatedFlag);
    onClose();
  };
  function validateEdits(): {
    isFlagNameEmpty: boolean;
    isFlagKeyEmpty: boolean;
    isButtonDisabled: boolean;
    hasEmptyKeys: boolean;
    hasDuplicateNames: boolean;
    hasInvalidSplits: boolean;
    splitsDoNotAddUpTo100: boolean;
  } {
    variants.forEach(variant => {
      var _variant$key;
      return variant.key = (_variant$key = variant.key) !== null && _variant$key !== void 0 ? _variant$key : "";
    }); // Variant key initially undefined when Add Variant is clicked, causing undefined ref on trim()
    const _hasEmptyKeys = variants.some(variant => variant.key.trim() === "");
    const names = variants.map(variant => variant.key.trim());
    const uniqueNames = new Set(names);
    const _hasDuplicateNames = uniqueNames.size !== names.length;
    const _hasInvalidSplits = variants.some(variant => isNaN(variant.split) || variant.split < 0 || variant.split > 1);
    const splitsTotal = variants.reduce((sum, variant) => sum + variant.split, 0);
    const _splitsDoNotAddUpTo100 = Math.abs(splitsTotal - 1) > 0.001; // Use small epsilon for floating point comparison

    const _isFlagNameEmpty = stagedFlag.name.trim() === "";
    const _isFlagKeyEmpty = stagedFlag.key.trim() === "";
    const _isButtonDisabled = _hasEmptyKeys || _hasDuplicateNames || _hasInvalidSplits || _splitsDoNotAddUpTo100 || _isFlagNameEmpty || _isFlagKeyEmpty;
    return {
      isFlagNameEmpty: _isFlagNameEmpty,
      isFlagKeyEmpty: _isFlagKeyEmpty,
      isButtonDisabled: _isButtonDisabled,
      hasEmptyKeys: _hasEmptyKeys,
      hasDuplicateNames: _hasDuplicateNames,
      hasInvalidSplits: _hasInvalidSplits,
      splitsDoNotAddUpTo100: _splitsDoNotAddUpTo100
    };
  }
  function handleChangeTestUserVariant(distinctId: string, variant: string, remove: boolean) {
    const newUsers = testUsers.map(user => {
      return {
        email: user.email,
        variant: user.distinctId === distinctId ? variant : user.variant,
        distinctId: user.distinctId
      };
    }).filter(user => {
      return !remove || user.distinctId !== distinctId;
    });
    setTestUsers(newUsers);
    const userObj: {
      [keys: string]: string;
    } = {};
    newUsers.map(user => {
      var _user$distinctId;
      userObj[(_user$distinctId = user.distinctId) !== null && _user$distinctId !== void 0 ? _user$distinctId : ""] = user.variant;
    });
    setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
      ruleset: _objectSpread(_objectSpread({}, stagedFlag.ruleset), {}, {
        test: {
          users: userObj
        }
      })
    }));
  }
  let fetchUserAbortSignal = new AbortController();
  // Given the distinct_ids for test users, fetch their emails
  useEffect(() => {
    async function callFetchUsers() {
      var _stagedFlag$ruleset$t2, _stagedFlag$ruleset$t3;
      fetchUserAbortSignal.abort();
      fetchUserAbortSignal = new AbortController();
      const response = await fetchUsers({
        abortSignal: fetchUserAbortSignal.signal,
        cohort: new Cohort({
          groups: [{
            type: SelectorTypeDefinitions.CohortGroup,
            groupingOperator: FiltersOperator.Or,
            filtersOperator: FiltersOperator.Or,
            event: {
              resourceType: ResourceType.Cohort,
              value: ALL_USERS_COHORT.value as string,
              label: ALL_USERS_COHORT.label.toString()
            },
            filters: [new PropertyFilter({
              filterOperator: StringOperator.Equals,
              filterValue: Object.keys((_stagedFlag$ruleset$t2 = (_stagedFlag$ruleset$t3 = stagedFlag.ruleset.test) === null || _stagedFlag$ruleset$t3 === void 0 ? void 0 : _stagedFlag$ruleset$t3.users) !== null && _stagedFlag$ruleset$t2 !== void 0 ? _stagedFlag$ruleset$t2 : {}),
              propertyDefaultType: PropertyType.String,
              propertyName: "$distinct_id",
              propertyType: PropertyType.String,
              resourceType: ResourceType.User
            })]
          }]
        }),
        includeAllUsers: true,
        sortColumn: {
          value: "$email",
          resourceType: "user"
        },
        columns: [{
          value: "$email",
          resourceType: "user"
        }, {
          value: "$distinct_id",
          resourceType: "user"
        }, {
          value: "$name",
          resourceType: "user"
        }],
        sortOrder: "ascending"
      });
      setTestUsers(testUsers.map(testUser => {
        var _testUser$distinctId, _testUser$distinctId2;
        let email = (_testUser$distinctId = testUser.distinctId) !== null && _testUser$distinctId !== void 0 ? _testUser$distinctId : "";
        response.forEach(user => {
          if (testUser.distinctId === user.$distinct_id) {
            email = user.$properties.$email;
          }
        });
        return {
          email,
          distinctId: (_testUser$distinctId2 = testUser.distinctId) !== null && _testUser$distinctId2 !== void 0 ? _testUser$distinctId2 : "",
          variant: testUser.variant
        };
      }));
    }
    try {
      var _stagedFlag$ruleset$t4, _stagedFlag$ruleset2;
      if (Object.keys((_stagedFlag$ruleset$t4 = (_stagedFlag$ruleset2 = stagedFlag.ruleset) === null || _stagedFlag$ruleset2 === void 0 || (_stagedFlag$ruleset2 = _stagedFlag$ruleset2.test) === null || _stagedFlag$ruleset2 === void 0 ? void 0 : _stagedFlag$ruleset2.users) !== null && _stagedFlag$ruleset$t4 !== void 0 ? _stagedFlag$ruleset$t4 : []).length > 0) {
        setTestUsersLoading(true);
        callFetchUsers().then(() => setTestUsersLoading(false));
      } else {
        setTestUsersLoading(false);
      }
    } catch (_err) {
      // TODO: Error toast
      setTestUsersLoading(false);
    }
  }, [flag]);
  function onAddOrRemoveVariant(): void {
    setStagedFlag(currentFlag => _objectSpread(_objectSpread({}, currentFlag), {}, {
      ruleset: _objectSpread({}, currentFlag.ruleset)
    }));
  }
  return <ModalReact open={isOpen} closeable modalType={ModalType.Modal} growToContent verticalAlignment={VerticalAlignment.Top} onStackableClosed={(ev: TODO) => {
    if (ev.target.localName === "mp-modal") {
      // so stackables in the modal can't close this modal
      onClose();
    }
  }} data-sentry-element="ModalReact" data-sentry-component="EditFeatureFlag" data-sentry-source-file="index.tsx">
      <div className={styles.editVariantsModal}>
        <h1 className={styles.header}>{i18n.t("feature-flags.page.edit.title")}</h1>

        <div className={styles.flagDetailsSection}>
          <div className={styles.flagSetupSection}>
            <div className={styles.flagDataInput}>
              <InputLabelReact fieldId='flag-name' label={i18n.t("feature-flags.page.config.name")} helpText={i18n.t("feature-flags.page.name-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <InputReact className={styles.fullWidth} fieldId='flag-name' value={stagedFlag.name} onInput={ev => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              name: ev.target.value
            }))} errorMessage={isFlagNameEmpty ? i18n.t("feature-flags.page.edit.error.empty-flag-name") : undefined} validationStatus={isFlagNameEmpty ? "invalid" : undefined} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            </div>
            <div className={styles.flagDataInput}>
              <InputLabelReact fieldId='flag-key' label={i18n.t("feature-flags.page.config.key")} helpText={i18n.t("feature-flags.page.key-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <InputReact className={styles.fullWidth} fieldId='flag-key' value={stagedFlag.key} onInput={ev => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              key: ev.target.value
            }))} errorMessage={isFlagKeyEmpty ? i18n.t("feature-flags.page.edit.error.empty-flag-key") : undefined} validationStatus={isFlagKeyEmpty ? "invalid" : undefined} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            </div>
            <div className={styles.flagDataInput}>
              <EvalContextSelect onSelect={val => {
              setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
                context: val !== null && val !== void 0 ? val : stagedFlag.context
              }));
            }} selectedEvalContext={stagedFlag.context} data-sentry-element="EvalContextSelect" data-sentry-source-file="index.tsx" />
            </div>
            <div className={styles.flagToggle}>
              <InputLabelReact fieldId='flag-status' label={i18n.t("feature-flags.page.config.status")} helpText={i18n.t("feature-flags.page.status-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <div className={styles.statusToggle}>
                <span className={styles.statusLabel}>{i18n.t("feature-flags.status.".concat(stagedFlag.status))}</span>
                <SwitchReact on={stagedFlag.status === "enabled"} onChange={handleStatusToggle} data-sentry-element="SwitchReact" data-sentry-source-file="index.tsx" />
              </div>
            </div>
          </div>

          {SHOULD_SHOW_SERVING_METHOD && <div className={styles.flagDetailsRow}>
              <div>
                <InputLabelReact fieldId='serving-method' label={i18n.t("feature-flags.page.config.serving-method")} />
                <SelectReact className={styles.dropdown} theme={SelectTheme.Select} alwaysShowCaret selectedOption={i18n.t("feature-flags.serving-method.".concat(stagedFlag.serving_method))} onChange={(value: TODO) => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              serving_method: value.detail.item.value
            }))} sections={[{
              items: [{
                label: i18n.t("feature-flags.serving-method.client"),
                isSelected: stagedFlag.serving_method === "client",
                value: "client"
              }, {
                label: i18n.t("feature-flags.serving-method.server"),
                isSelected: stagedFlag.serving_method === "server",
                value: "server"
              }]
            }]} />
              </div>
            </div>}
        </div>

        <Variants variants={variants} setVariants={setVariants} onAddOrRemoveVariant={onAddOrRemoveVariant} data-sentry-element="Variants" data-sentry-source-file="index.tsx" />

        {isButtonDisabled && <div className={styles.validationErrors}>
            {hasEmptyKeys && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.empty-names")}</div>}
            {hasDuplicateNames && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.duplicate-names")}</div>}
            {hasInvalidSplits && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.invalid-splits")}</div>}
            {splitsDoNotAddUpTo100 && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.splits-not-100")}</div>}
          </div>}

        {((_currentProject$get$v = currentProject.get().value) === null || _currentProject$get$v === void 0 ? void 0 : _currentProject$get$v.hasFeatureFlag(ProjectFeatureFlag.FeatureFlagsTestUsers)) && <FeatureFlagTestUsers canUpdate variantList={variants.map(variant => variant.key)} testUsers={testUsers} isLoadingTestUsers={testUsersLoading} handleAdd={users => {
        setTestUsers(users);
        const newUsers: {
          [keys: string]: string;
        } = {};
        users.map(user => {
          var _user$distinctId2;
          newUsers[(_user$distinctId2 = user.distinctId) !== null && _user$distinctId2 !== void 0 ? _user$distinctId2 : ""] = user.variant;
        });
        setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
          ruleset: _objectSpread(_objectSpread({}, stagedFlag.ruleset), {}, {
            test: {
              users: newUsers
            }
          })
        }));
      }} handleChangeVariant={handleChangeTestUserVariant} />}

        <div className={styles.actionsContainer}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={onClose} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {i18n.t("common.cancel")}
          </ButtonReact>
          <ButtonReact theme={ButtonTheme.Primary} onClick={handleUpdate} disabled={isButtonDisabled} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {i18n.t("common.save")}
          </ButtonReact>
        </div>
      </div>
    </ModalReact>;
}
interface VariantsProps {
  variants: Variant[];
  setVariants: (variants: Variant[]) => void;
  onAddOrRemoveVariant?: () => void;
}
function Variants(_ref2: VariantsProps) {
  let {
    variants,
    setVariants,
    onAddOrRemoveVariant
  }: VariantsProps = _ref2;
  const i18n = useTranslation();
  const handleNameChange = (index: number, newName: string) => {
    const variant = variants[index];
    if (variant.key === newName) return;
    const newVariants = [...variants];
    newVariants[index] = _objectSpread(_objectSpread({}, variant), {}, {
      key: newName
    });
    setVariants(newVariants);
  };
  const handleStickyToggle = (index: number, enabled: boolean) => {
    const newVariants = [...variants];
    newVariants[index] = _objectSpread(_objectSpread({}, newVariants[index]), {}, {
      is_sticky: enabled
    });
    setVariants(newVariants);
  };
  const handleSplitChange = (index: number, splitStr: string) => {
    const splitValue = parseFloat(splitStr);
    if (isNaN(splitValue)) return;
    const newVariants = [...variants];
    newVariants[index] = _objectSpread(_objectSpread({}, variants[index]), {}, {
      split: splitValue
    });
    setVariants(newVariants);
  };
  const handleDescriptionChange = (index: number, descStr: string) => {
    const variant = variants[index];
    if (variant.description === descStr) return;
    const newVariants = [...variants];
    newVariants[index] = _objectSpread(_objectSpread({}, variants[index]), {}, {
      description: descStr
    });
    setVariants(newVariants);
  };
  const handleAddVariant = () => {
    const newVariant: Variant = {
      key: "",
      description: "",
      split: 0,
      is_sticky: false,
      is_control: false,
      value: ""
    };
    setVariants([...variants, newVariant]);
    onAddOrRemoveVariant === null || onAddOrRemoveVariant === void 0 || onAddOrRemoveVariant();
  };
  const handleRemoveVariant = (index: number) => {
    if (variants.length <= 1) return; // Don't allow removing the last variant

    const newVariants = variants.filter((_, i) => i !== index);
    setVariants(newVariants);
    onAddOrRemoveVariant === null || onAddOrRemoveVariant === void 0 || onAddOrRemoveVariant();
  };
  return <>
      <h2 className={styles.sectionHeader}>{i18n.t("feature-flags.page.variants.title")}</h2>
      <h3 className={styles.sectionSubheader}>{i18n.t("feature-flags.page.variants.configurations")}</h3>
      <h3 className={styles.sectionSubtext}>{i18n.t("feature-flags.page.variants.allocations-percents")}</h3>
      <div className={styles.variantsContainer}>
        <div className={styles.variantRow}>
          <div className={styles.inputColumn}>
            <div className={styles.inputLabel} />
            {variants.map((_variant, index) => <div className={styles.variantRow} key={index + "icon"}>
                <div className={styles.variantLabel}>{indexToLabel(index)}</div>
              </div>)}
          </div>
          <div className={styles.inputColumn}>
            <div className={styles.inputLabel}>
              <InputLabelReact label={i18n.t("feature-flags.page.variants.name")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            </div>
            {variants.map((variant, index) => <div className={styles.variantRow} key={index + "name"}>
                <div className={styles.nameInput}>
                  <div className={styles.splitInputContainer}>
                    <InputReact disabled={Boolean(variant.is_control) || Boolean(variant.is_sticky)} fieldId={"variant-name-".concat(index)} value={variant.key} onInput={ev => handleNameChange(index, ev.target.value)} />
                  </div>
                </div>
              </div>)}
          </div>
          <div className={styles.descriptionColumn}>
            <div className={styles.inputLabel}>
              <InputLabelReact label={i18n.t("feature-flags.page.variants.description")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            </div>
            {variants.map((variant, index) => {
            var _variant$description2;
            return <div className={styles.variantRow} key={index + "description"}>
                <div className={styles.descriptionInput}>
                  <div className={styles.splitInputContainer}>
                    <InputReact className={styles.mpInput} fieldId={"variant-description-".concat(index)} value={(_variant$description2 = variant.description) !== null && _variant$description2 !== void 0 ? _variant$description2 : ""} placeholder={i18n.t("feature-flags.page.variants.description")} onInput={ev => handleDescriptionChange(index, ev.target.value)} />
                  </div>
                </div>
              </div>;
          })}
          </div>
          <div className={styles.inputColumn}>
            <div className={styles.inputLabel}>
              <InputLabelReact label={i18n.t("feature-flags.page.variants.split")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            </div>
            {variants.map((variant, index) => <div className={styles.variantRow} key={index + "split"}>
                <div className={styles.splitInput}>
                  <div className={styles.splitInputContainer}>
                    <InputReact fieldId={"variant-split-".concat(index)} value={String(variant.split * 100)} onInput={ev => {
                  const percentageValue = parseFloat(ev.target.value);
                  if (!isNaN(percentageValue)) {
                    handleSplitChange(index, (percentageValue / 100).toString());
                  }
                }} suffixRenderer={
                // @ts-ignore "No overload matches this call"
                () => h("div", {
                  style: {
                    marginRight: "8px"
                  }
                }, "%")} />
                  </div>
                </div>
              </div>)}
          </div>
          <div className={styles.inputColumn}>
            <div className={styles.inputLabel}>
              <InputLabelReact label={i18n.t("feature-flags.page.variants.sticky")} help-text={i18n.t("feature-flags.page.variants.sticky-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
            </div>
            {variants.map((variant, index) => <div className={styles.variantRow} key={index + "sticky"}>
                <div className={styles.statusToggle}>
                  <SwitchReact disabled={Boolean(variant.is_control)} on={Boolean(variant.is_sticky)} onChange={ev => handleStickyToggle(index, Boolean((ev as SwitchChangeEvent).detail.on))} />
                </div>
              </div>)}
          </div>
          <div className={styles.inputColumn}>
            <div className={styles.inputLabel} />
            {variants.map((_variant, index) => <div className={styles.variantRow} key={index + "delete"}>
                <SvgIconReact icon='trashcan' onClick={() => index > 0 ? handleRemoveVariant(index) : {}} className={index > 0 ? styles.variantDeleteIcon : styles.variantDeleteIconDisabled} />
              </div>)}
          </div>
        </div>
        <div className={styles.variantActions}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={handleAddVariant} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            <SvgIconReact icon='plus' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
            {i18n.t("feature-flags.page.variants.add")}
          </ButtonReact>
        </div>
      </div>
    </>;
}
function indexToLabel(index: number): string {
  let count = index;
  let letters = "";
  while (count >= 0) {
    letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[count % 26] + letters;
    count = Math.floor(count / 26) - 1;
  }
  return letters;
}